<template>
    <div
        class="apprentice-simple-card profile-list"
        v-if="loaded">
        <div
            class="heading"
            v-if="!viewPublicly">
            <h4 class="title">{{ $t('profile.my_personal_timeline') }}</h4>
            <div
                class="user-profile-actions"
                v-if="!viewPublicly && !teacherAccess">
                <design-2021-profile-timeline-custom-banner-modal-component
                    :banner-in="banner"></design-2021-profile-timeline-custom-banner-modal-component>
                <b-button
                    class="btn-share btn-share-timeline"
                    v-b-tooltip.hover
                    variant="primary-icon-only"
                    :title="$t('profile.share_timeline')"
                    @click="showShareModal">
                </b-button>
                <design-2021-profile-share-modal-component
                    :school-years="schoolYears"
                    :categories-in="categoriesIn"
                    :seasons-in="seasonsIn"
                    :cards-in="cards">
                </design-2021-profile-share-modal-component>

                <b-button
                    class="btn-add btn-add-timeline-item"
                    v-b-tooltip.hover
                    v-b-modal="'profile-add-card-modal'"
                    variant="primary-icon-only"
                    :title="$t('profile.add_item_to_timeline')">
                </b-button>

                <b-button
                    class="btn-download btn-download-pdf"
                    variant="primary-icon-only"
                    title="Download PDF van de tijdlijn"
                    @click="downloadPDF"></b-button>

                <design-2021-profile-add-card-component
                    v-on:add-card="addCard"
                    :categories-in="categoriesIn"
                    :seasons-in="seasonsIn">
                </design-2021-profile-add-card-component>
            </div>
            <div
                class="user-profile-actions"
                v-else>
                <b-button
                    class="btn-download btn-download-pdf"
                    variant="primary-icon-only"
                    title="Download PDF van de tijdlijn"
                    @click="downloadPDF"></b-button>
            </div>
        </div>
        <div class="body">
            <div class="row">
                <div class="col-12 order-1 order-lg-0 col-lg-9 user-timeline">
                    <div
                        class="heading"
                        v-if="viewPublicly">
                        <h4 class="title">{{ $t('profile.my_personal_timeline') }}</h4>
                    </div>
                    <div
                        class="user-timeline-intro muted-text"
                        v-if="!teacherAccess">
                        {{ $t('profile.timeline_add_items') }}
                    </div>
                    <div
                        class="user-timeline-intro muted-text"
                        v-else>
                        {{ $t('profile.timeline_collect_experiences') }}
                    </div>
                    <div
                        class="timeline-profile"
                        :class="schoolYears.length === 0 && 'hide-timeline-bar'">
                        <div
                            class="not-found with-header no-timeline-profile-found"
                            v-if="schoolYears.length === 0">
                            <div
                                class="btn-add-circle icon"
                                v-b-modal="'profile-add-card-modal'"></div>
                            <div class="alert-notice">
                                {{ $t('profile.no_timeline_items_found') }}
                            </div>
                        </div>

                        <div
                            class="schoolyear"
                            v-for="schoolYear in filteredCardsByYear"
                            v-else>
                            <div
                                class="timeline-profile-wrapper timeline-profile-inverted timeline-profile-wrapper-danger timeline-profile-year">
                                {{ schoolYear.name }}
                            </div>
                            <div
                                class="timeline-profile-wrapper timeline-profile-inverted timeline-profile-wrapper-warning"
                                v-for="card in schoolYear.cards"
                                :id="`card-${card.id}`">
                                <div class="timeline-profile-date d-none d-lg-block">
                                    {{ card.date | date_written }}
                                </div>
                                <div
                                    class="timeline-profile-badge d-none d-lg-block"
                                    :style="cssVars(card)"></div>
                                <div
                                    class="timeline-profile-panel"
                                    v-bind:style="{ borderTopColor: card.category.color }">
                                    <div class="timeline-profile-heading">
                                        <div class="headings-content">
                                            <div class="timeline-profile-date d-block d-lg-none">
                                                {{ card.date | date_written }}
                                            </div>
                                            <h6 class="timeline-profile-title">{{ card.title }}</h6>
                                            <div class="timeline-profile-category">{{ card.category.title }}</div>
                                        </div>
                                        <div
                                            class="ellipsis-nav timeline-nav"
                                            v-if="!teacherAccess && !viewPublicly">
                                            <b-dropdown
                                                class="ellipsis-nav"
                                                size="lg"
                                                no-caret
                                                variant="primary"
                                                text="right align"
                                                right>
                                                <template #button-content>
                                                    <i class="fas fa-ellipsis-v"></i>
                                                </template>
                                                <b-dropdown-item-button
                                                    class="btn-edit-profile text-primary"
                                                    @click="openEditModal(card)">
                                                    <i class="btn-edit"></i>{{ $t('common.edit') }}
                                                </b-dropdown-item-button>
                                                <b-dropdown-item-button
                                                    class="btn-remove-profile text-danger"
                                                    @click="deleteCard(card.id)">
                                                    <i class="btn-remove"></i>{{ $t('common.delete') }}
                                                </b-dropdown-item-button>
                                            </b-dropdown>
                                        </div>
                                    </div>
                                    <div class="timeline-profile-body">
                                        <design-2021-profile-card-component
                                            v-if="card.type_id === 1"
                                            :card="card"></design-2021-profile-card-component>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        class="new-timeline-item"
                        v-if="!viewPublicly && !teacherAccess">
                        <b-button
                            class="btn-add btn-new-profile-timeline-item"
                            v-b-modal="'profile-add-card-modal'"
                            variant="primary"
                            >{{ $t('profile.add_timeline_item') }}
                        </b-button>
                    </div>
                    <design-2021-profile-update-card-component
                        ref="profile-update-card-modal"
                        :categories-in="categoriesIn"
                        :seasons-in="seasonsIn"
                        @update-card="updateCard">
                    </design-2021-profile-update-card-component>
                </div>
                <div
                    class="col-12 order-0 order-lg-1 col-lg-3 user-timeline-filters"
                    :class="viewPublicly && 'public-access'">
                    <div
                        class="user-avatar"
                        v-if="viewPublicly">
                        <design-2021-profile-header-component
                            :teacher-access="teacherAccess"
                            :user-credentials-in="userCredentialsIn">
                        </design-2021-profile-header-component>
                    </div>

                    <b-button
                        class="btn-filter btn-user-timeline-filters-toggle"
                        v-b-toggle.user-timeline-filters
                        variant="primary">
                        Filters
                    </b-button>
                    <b-collapse
                        class="filtered-content"
                        id="user-timeline-filters"
                        visible>
                        <div class="filtered-categories">
                            <p class="title">{{ $t('profile.category') }}</p>

                            <b-form-checkbox-group
                                class="category-filters"
                                v-model="selectedCategories"
                                :options="categories">
                            </b-form-checkbox-group>
                        </div>

                        <div class="filtered-schoolyears">
                            <p class="title">{{ $t('profile.school_year') }}</p>
                            <div
                                class="schoolyear-filter-item"
                                v-for="schoolYear in schoolYears">
                                <b-form-checkbox
                                    class="apprentice-checkbox profile-school-year-filter-check"
                                    v-model="selectedSchoolYears"
                                    :checked="schoolYear.checked"
                                    :value="schoolYear.name">
                                    <span>{{ schoolYear.name }}</span>
                                </b-form-checkbox>
                            </div>
                        </div>
                    </b-collapse>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Design2021CustomBannerUploadWidgetComponent from '../common/design-2021-custom-banner-upload-widget-component.vue';
import moment from 'moment';

export default {
    name: 'design-2021-profile-timeline-component',
    components: { Design2021CustomBannerUploadWidgetComponent },
    props: [
        'cardsIn',
        'categoriesIn',
        'dropCats',
        'teacherAccess',
        'viewPublicly',
        'banner',
        'image',
        'selectedCategoriesIn',
        'seasonsIn',
        'userCredentialsIn',
    ],
    data() {
        return {
            cards: this.cardsIn,
            selectedCategories: this.getSelectedCategories(),
            selectedSchoolYears: this.seasonsIn,
            categories: this.getAllCategories(),
            latestAvailableDate: this.getLatestDate(),
            loaded: false,
        };
    },

    mounted() {
        if (!this.loaded) {
            this.cards = this.getAllCards();
            this.selectedSchoolYears = this.getAllSchoolSeasons();

            this.loaded = true;
        }
    },

    methods: {
        getLatestDate() {
            const today = new Date();
            const month = moment(today).month() + 1;
            let year = moment(today).year();

            if (month >= 8) {
                year = year + 1;
            }
            return year + '-07-31';
        },

        getSelectedCategories() {
            if (this.viewPublicly && this.selectedCategoriesIn.length > 0) {
                return this.selectedCategoriesIn.map((category) => category.id);
            }

            const selectedCategoriesIds = this.categoriesIn.map((category) => category.id);

            categoriesIdLoop: for (const categoryIn of this.categoriesIn) {
                for (const cat of selectedCategoriesIds) {
                    if (categoryIn.category_id !== cat) {
                        selectedCategoriesIds.push(0);
                        break categoriesIdLoop;
                    }
                }
            }

            return selectedCategoriesIds;
        },

        getAllCategories() {
            let categories = [];

            if (this.viewPublicly && this.selectedCategoriesIn.length > 0) {
                categories = this.selectedCategoriesIn.map((category) => {
                    return {
                        value: category.id,
                        text: category.title,
                    };
                });
            } else {
                categories = this.categoriesIn.map((category) => {
                    return {
                        value: category.id,
                        text: category.title,
                    };
                });

                categoriesLoop: for (const categoryIn of this.categoriesIn) {
                    for (const cat of categories) {
                        if (categoryIn.category_id !== cat.value) {
                            categories.push({
                                value: 0,
                                text: this.$t('profile.no_category'),
                            });
                            break categoriesLoop;
                        }
                    }
                }
            }

            return categories;
        },

        getAllCards() {
            if (this.cardsIn.length >= 1) {
                return this.cardsIn.map((card) => {
                    if (!card.category) {
                        card.category = {
                            color: '#07A1FF',
                            title: this.$t('profile.no_category'),
                        };
                    }

                    return {
                        ...card,
                        school_year: this.getSchoolYear(card),
                    };
                });
            }

            return [];
        },

        getSchoolYear(card) {
            if (this.seasonsIn.length >= 1 && this.cardsIn.length >= 1) {
                const seasons = this.seasonsIn.map((season) => season.season);
                const startSeason = moment(card.date).format('YYYY') + '-08-01';
                const newSeason = moment(card.date).isSameOrAfter(startSeason);
                const beforeLastAvailableDate = moment(card.date).isBefore(this.latestAvailableDate);

                if (!beforeLastAvailableDate) {
                    const month = moment(card.date).month();
                    const year = moment(card.date).year();

                    if (month >= 8) {
                        const secondYear = year + 1;

                        return year + '-' + secondYear;
                    } else {
                        const firstYear = year - 1;

                        return firstYear + '-' + year;
                    }
                } else {
                    for (const schoolSeason of seasons) {
                        const years = schoolSeason.split('-');

                        if (newSeason) {
                            if (moment(card.date).isSameOrAfter(years[0], 'year')) {
                                return schoolSeason;
                            }
                        } else {
                            if (moment(card.date).subtract(1, 'year').isSameOrAfter(years[0], 'year')) {
                                return schoolSeason;
                            }
                        }
                    }
                }

                return this.$t('profile.unfamiliar');
            }
        },

        addCard(data, headers) {
            this.alert.saving();
            this.axios.post(this.routes.profileCard(), data, { headers: headers }).then(
                (response) => {
                    this.cards.push({
                        ...response.data,
                        school_year: this.getSchoolYear(response.data),
                    });
                    this.cards = this.cards.sort((a, b) => {
                        return new Date(b.date) - new Date(a.date);
                    });

                    this.alert.success();
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        openEditModal(card) {
            this.$refs['profile-update-card-modal'].show(card);
        },

        updateCard(data, headers) {
            this.alert.saving();
            this.axios.post(this.routes.profileCardUpdate(data.get('id')), data, { headers: headers }).then(
                (response) => {
                    this.cards = this.cards.map((card) => {
                        if (card.id === response.data.id) {
                            card = {
                                ...response.data,
                                school_year: this.getSchoolYear(response.data),
                            };
                        }

                        return card;

                        // return(card.id !== data.id) ? card : {
                        //     ...response.data,
                        //     school_year: this.getSchoolYear(response.data),
                        // }
                    });

                    this.cards = this.cards.sort((a, b) => {
                        return new Date(b.date) - new Date(a.date);
                    });

                    this.alert.success();
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        deleteCard(id) {
            this.alert
                .confirm(this.$t('profile.delete_item'), this.$t('profile.are_you_sure_you_want_to_delete_this_item'))
                .then((confirm) => {
                    if (confirm) {
                        this.alert.saving();
                        this.axios.delete('/profile/deletecard/' + id).then((response) => {
                            this.cards = this.cards.filter((card) => card.id !== id);
                            this.alert.success();
                        });
                    }
                });
        },

        showShareModal() {
            this.$bvModal.show('profile-share-modal');
        },

        cssVars(timeline) {
            return {
                '--border-color': timeline.category.color,
            };
        },

        getAllSchoolSeasons() {
            if (this.seasonsIn.length >= 1 && this.cards.length >= 1) {
                let seasons = this.seasonsIn.map((season) => season.season);
                seasons = seasons.filter((season) => this.cards.some((card) => card.school_year === season));

                return seasons;
            }

            return [];
        },

        downloadPDF() {
            window.open(this.routes.profileDownloadPDF(this.userCredentialsIn.id), '_blank');
        },
    },

    computed: {
        filteredCardsByYear() {
            let years = [];

            let cards = this.cards
                .filter((card) => this.selectedSchoolYears.includes(card.school_year))
                .map((card) => {
                    if (
                        this.categories.length >= 1 &&
                        this.categories.map((category) => category.value).indexOf(card.category_id) === -1
                    ) {
                        card.category = {
                            color: '#07A1FF',
                            id: 0,
                            title: this.$t('profile.no_category'),
                        };
                        return {
                            ...card,
                        };
                    } else {
                        return card;
                    }
                })
                .filter((card) => this.selectedCategories.includes(card.category.id));

            for (const card of cards) {
                if (!years.some((year) => year.name === card.school_year)) {
                    years.push({
                        name: card.school_year,
                        cards: [],
                    });
                }
                years.find((year) => year.name === card.school_year).cards.push(card);
            }

            return years;
        },

        schoolYears() {
            let years = [];

            for (const card of this.cards) {
                if (!years.some((year) => year.name === card.school_year)) {
                    years.push({
                        name: card.school_year,
                        selected: this.selectedSchoolYears.includes(card.school_year),
                    });
                }
            }

            return years;
        },
    },
};
</script>

<style scoped lang="scss">
.no-timeline-profile-found {
    margin-top: map-get($grid-gutter-spacing, 'twenty-one') * 2;
}

.timeline-nav ::v-deep {
    margin-top: -#{map-get($grid-gutter-spacing, 'eight')};

    .dropdown-toggle {
        font-size: $base-font-size;

        &:hover {
            color: var(--primaryLightest) !important;
        }
    }

    .dropdown-item {
        display: flex;
        flex-direction: row;
        align-items: center;

        i {
            margin-right: map-get($grid-gutter-spacing, 'eight');
            width: 20px;

            &:before {
                margin-right: 0;
            }
        }
    }

    .btn-edit-profile {
        i {
            color: var(--primary);
        }
    }

    //&:hover {
    //    ::v-deep
    //}
}

.user-timeline-filters {
    margin-bottom: map-get($grid-gutter-spacing, 'eight');

    .btn-user-timeline-filters-toggle {
        width: 100%;
        font-weight: $font-weight-medium;
    }

    .filtered-content {
        margin-top: map-get($grid-gutter-spacing, 'eight');
        margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');
    }

    .title {
        font-size: $base-font-size;
        padding-bottom: map-get($grid-gutter-spacing, 'ten');
        margin-bottom: map-get($grid-gutter-spacing, 'ten');
        border-bottom: 1px solid $border-color;
    }

    .category-filters ::v-deep {
        display: flex;
        flex-direction: column;

        .custom-checkbox {
            label {
                &:before,
                &:after {
                    top: 0;
                }
            }
        }
    }

    .schoolyear-filter-item {
        &:not(:last-child) {
            margin-bottom: map-get($grid-gutter-spacing, 'ten');
        }
    }

    .filtered-categories {
        margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');
    }

    &.public-access {
        margin-top: 0;
        margin-bottom: map-get($grid-gutter-spacing, 'forty-two');

        .user-avatar {
            margin-bottom: map-get($grid-gutter-spacing, 'forty-two');
        }
    }
}

.user-timeline-intro {
    margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');
    margin-right: 0;
}

.heading {
    margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');
    justify-content: space-between;

    &,
    .user-profile-actions {
        display: flex;
        align-items: center;
    }

    .user-profile-actions {
        flex-flow: row;

        .btn-share-timeline,
        .btn-add-timeline {
            font-size: 1.125rem;
        }

        .btn-custom-banner {
            font-size: 1.3125rem;
        }

        .btn:not(:last-child) {
            margin-right: map-get($grid-gutter-spacing, 'fifteen');
        }
    }
}

.timeline-profile {
    position: relative;
    margin-right: 0;
    padding-bottom: map-get($grid-gutter-spacing, 'twenty-one');

    > div:not(:first-child) {
        padding-top: map-get($grid-gutter-spacing, 'twenty-one');
    }

    .timeline-profile-year {
        margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');
        font-size: 1.25rem;
        font-weight: $font-weight-medium;
    }

    .timeline-profile-wrapper {
        display: block;
        margin-top: 0;
        position: relative;
        width: 100%;
        margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');
    }

    .timeline-profile-panel {
        border-radius: $border-radius-content;
        border-top-left-radius: 0 !important;
        border-top-right-radius: 0 !important;
        border-top: 3px solid;
        padding: map-get($grid-gutter-spacing, 'twenty-one');
        position: relative;
        background: $secondary-background-color;
        box-shadow: 1px 2px 35px 0 $border-color;

        .timeline-profile-date {
            text-align: left;
            margin-bottom: map-get($grid-gutter-spacing, 'eight');
            font-weight: $font-weight-medium;
            font-size: $default-font-size;
            line-height: 1;
        }

        .timeline-profile-heading {
            display: flex;
            justify-content: space-between;
            align-content: center;
        }

        .timeline-profile-title {
            margin-top: 0;
            margin-bottom: map-get($grid-gutter-spacing, 'eight');
            color: $text-color;
        }

        .timeline-profile-category {
            color: $text-muted-color;
            margin-bottom: map-get($grid-gutter-spacing, 'eight');
        }

        .timeline-profile-body {
            p + p {
                margin-top: map-get($grid-gutter-spacing, 'eight');
            }

            ul {
                margin-bottom: 0;
            }
        }

        .timeline-profile-footer {
            border-top: 1px solid $border-color;
            padding-top: map-get($grid-gutter-spacing, 'ten');
            margin-top: map-get($grid-gutter-spacing, 'twenty-one');

            span {
                font-size: 0.6875rem;
            }

            i {
                cursor: pointer;
            }

            .tools {
                display: flex;

                .btn:not(:last-child) {
                    margin-right: map-get($grid-gutter-spacing, 'fifteen');
                }
            }
        }

        &:before {
            position: absolute;
            top: 0;
            height: 2px;
            content: '';
            left: 0;
            right: 0;
        }
    }

    &.hide-timeline-bar:before {
        display: none;
    }
}

.ellipsis-nav ::v-deep {
    .dropdown-menu {
        li {
            .btn-edit-profile i {
                color: var(--primary);
            }

            .btn-remove-profile i {
                color: $danger;
            }
        }
    }
}

.new-timeline-item {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: map-get($grid-gutter-spacing, 'twenty-one');
}

@media (min-width: map-get($grid-breakpoints, 'lg')) {
    .user-timeline-intro {
        margin-right: 6px;
    }

    .user-timeline-filters {
        margin-top: map-get($grid-gutter-spacing, 'fifteen') * -1;
        border-left: 2px solid $border-color;
    }

    .timeline-profile {
        margin-right: 6px;

        &:before {
            top: 0;
            bottom: 0;
            position: absolute;
            content: ' ';
            width: 3px;
            left: 61px;
            margin-left: -1.5px;
            height: calc(100% - 21px);
            background-color: $border-color;
        }
    }

    .timeline-profile-wrapper {
        padding-right: 90px;
    }

    .timeline-profile-year {
        background: $white;
        height: 50px;
        font-size: 20px;
        padding-left: 0 !important;
        left: -2px;
        margin-bottom: map-get($grid-gutter-spacing, 'ten') !important;
    }

    .timeline-profile-panel {
        top: 16px;

        &:after {
            content: '';
            border-top: 1em solid $secondary-background-color;
            border-left: 1em solid transparent;
            left: -13px;
            position: absolute;
            display: block;
            top: 0;
        }
    }

    .timeline-profile-badge {
        width: 22px;
        height: 22px;
        position: absolute;
        top: 5px;
        left: 51px;
        z-index: 10;
        border-top-right-radius: 50%;
        border-top-left-radius: 50%;
        border-bottom-right-radius: 50%;
        border-bottom-left-radius: 50%;
        border: 4px solid var(--border-color);
        background-color: $white;

        &:before {
            content: '';
            height: 30px;
            width: 30px;
            background: transparent;
            display: block;
            position: absolute;
            left: -8px;
            top: -8px;
            z-index: -1;
            border: 5px solid $white;
            border-radius: 25px;
        }
    }

    .timeline-profile-date {
        margin-left: -102px;
        margin-top: map-get($grid-gutter-spacing, 'eight');
        float: left;
        position: relative;
        max-width: 52px;
        font-size: 0.8125rem;
        line-height: 1.3;
        color: $text-muted-color;
    }

    .timeline-profile .timeline-profile-wrapper.timeline-profile-inverted {
        padding-right: 0;
        padding-left: 100px;
    }

    .user-timeline-filters {
        &.public-access {
            margin-bottom: 0;
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'xl')) {
    .user-timeline-filters {
        .user-avatar,
        .btn-user-timeline-filters-toggle,
        .filtered-content {
            max-width: 350px;
            margin-left: auto;
            margin-right: auto;
        }
    }
}
</style>
